/** @jsx isolateComponent */
import { isolateComponent } from '@zapier/style-encapsulation';
import { daysFromNow } from '@zapier/date';
import { Alert, Link } from '@zapier/design-system';

import type { Result } from '../useAlertBanner/alertBannerNotificationsQuery';
import { buildUpgradeUrl, formatPrice } from './utils';
import { BannerProps } from './types';

export const shouldRender = (data: Result['data']) => {
  const { currentAccount } = data;
  return (
    currentAccount.isTrialingCompanies &&
    currentAccount.companiesTrialEndDate &&
    ['owner', 'admin'].includes(currentAccount.currentUserRole)
  );
};

export const Component = function CompaniesTrialing(props: BannerProps) {
  const { data } = props;
  const { currentAccount } = data;
  const { companiesTrialEndDate } = currentAccount;
  const trialDaysRemaining = daysFromNow(companiesTrialEndDate!);
  const daysText = trialDaysRemaining === 1 ? 'day' : 'days';
  const title = `${trialDaysRemaining} ${daysText} left in your Company trial`;
  // @ref ul-1: Divide by 12 since `nextCompanyPlan` should always be annual.
  const nextPlanAmount = (data.nextCompanyPlan?.amount || 0) / 12;
  const upgradeUrl = buildUpgradeUrl({
    currentPlanName: currentAccount.plan.name,
    upgradePlanName: data.nextCompanyPlan?.name,
    interval: currentAccount.plan.interval,
  });

  const onUpgrade = () => {
    props.trackEvent('Trial: clicked upgrade from Company trial banner');
  };

  const onRequestDemo = () => {
    props.trackEvent('company-trial-guide', {
      accountId: currentAccount.id,
      profileId: data.currentUser.id,
      isOwner: currentAccount.currentUserRole === 'owner',
    });
  };

  return (
    <Alert layout={props.layout} title={title} type="info">
      <Link href={upgradeUrl} onClick={onUpgrade}>
        Upgrade
      </Link>
      {` anytime for as low as $${formatPrice(nextPlanAmount)}/month. `}
      {'Have questions about the Company plan? '}
      <Link
        href="/app/company-account-setup/getting-started"
        onClick={onRequestDemo}
      >
        Request a demo
      </Link>
    </Alert>
  );
};
